/**
 * The Mermaid theme, kept in the document itself as an init directive on its first line, so it
 * travels with share links, previews, thumbnails and exports. Rendering stays `securityLevel:
 * 'strict'`: Mermaid 11 lets a directive change `theme` but ignores the keys it marks as secure,
 * such as `securityLevel`. Only the `theme` key is ever written here; every other key of a
 * directive is left exactly as the author wrote it.
 */

export const MERMAID_THEMES = [
  'default',
  'neutral',
  'dark',
  'forest',
  'base',
] as const;
export type MermaidTheme = (typeof MERMAID_THEMES)[number];

/** A first-line `%%{init: {...}}%%` or `%%{initialize: {...}}%%`, with the object's opening brace. */
const DIRECTIVE = /^(\s*%%\{\s*(?:init|initialize)\s*:\s*\{)(.*)(\}\s*\}%%)/;
const THEME_ENTRY = /(["'])theme\1(\s*:\s*)(["'])([^"']*)\3/g;

/** The brace depth at one position of the directive body, ignoring braces inside strings. */
function depthAt(body: string, position: number): number {
  let depth = 0;
  let quote: string | null = null;
  for (let index = 0; index < position; index += 1) {
    const character = body[index]!;
    if (quote) {
      if (character === quote) quote = null;
    } else if (character === '"' || character === "'") quote = character;
    else if (character === '{') depth += 1;
    else if (character === '}') depth -= 1;
  }
  return depth;
}

/**
 * The directive's own `theme` entry. A `theme` key nested inside another setting, such as
 * `flowchart`, is not the diagram theme and Mermaid ignores it there, so it never counts.
 */
function topLevelThemeEntry(body: string): RegExpMatchArray | null {
  for (const match of body.matchAll(THEME_ENTRY)) {
    if (depthAt(body, match.index) === 0) return match;
  }
  return null;
}

function firstLine(source: string): { line: string; rest: string } {
  const newline = source.indexOf('\n');
  return newline === -1
    ? { line: source, rest: '' }
    : { line: source.slice(0, newline), rest: source.slice(newline) };
}

function isTheme(value: string): value is MermaidTheme {
  return (MERMAID_THEMES as readonly string[]).includes(value);
}

/** The theme the first-line directive names, or `default` when it names none this picker offers. */
export function mermaidTheme(source: string): MermaidTheme {
  const directive = DIRECTIVE.exec(firstLine(source).line);
  const theme = directive ? topLevelThemeEntry(directive[2]!)?.[4] : undefined;
  return theme && isTheme(theme) ? theme : 'default';
}

/** The source with its theme set: the directive's `theme` replaced, added to it, or a new directive. */
export function withMermaidTheme(source: string, theme: MermaidTheme): string {
  const { line, rest } = firstLine(source);
  const directive = DIRECTIVE.exec(line);
  if (!directive) return `%%{init: {"theme":"${theme}"}}%%\n${source}`;
  const [whole, opening, body, closing] = directive as unknown as [
    string,
    string,
    string,
    string,
  ];
  const entry = topLevelThemeEntry(body);
  const nextBody = entry
    ? `${body.slice(0, entry.index)}${entry[1]}theme${entry[1]}${entry[2]}${entry[3]}${theme}${entry[3]}${body.slice(entry.index! + entry[0].length)}`
    : `"theme":"${theme}"${body.trim() ? `, ${body}` : ''}`;
  return `${opening}${nextBody}${closing}${line.slice(whole.length)}${rest}`;
}
